import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';

import ChatControls from '../ChatControls';
import { getRandomSamplePrompts, type SamplePrompt } from '../utils';

interface ChatDefaultStateProps {
  firstName: string;
  greeting: string;
  handleSubmit: (e: React.FormEvent, message?: string) => void;
  input: string;
  isLoading: boolean;
  isStreaming: boolean;
  isReady: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
}

const ChatDefaultState = ({
  firstName,
  greeting,
  handleSubmit,
  input,
  isLoading,
  isStreaming,
  isReady,
  setInput,
}: ChatDefaultStateProps): JSX.Element => {
  const theme = useTheme();

  // Get 4-5 random sample prompts
  const samplePrompts = useMemo(() => getRandomSamplePrompts(5), []);

  // Handle chip click - set input and auto-submit
  const handlePromptClick = (prompt: SamplePrompt) => {
    setInput(prompt.actualPrompt);
    // Create a synthetic event to submit
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent;
    // Pass the actual prompt directly to handleSubmit to avoid state update timing issues
    handleSubmit(syntheticEvent, prompt.actualPrompt);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        minHeight: '100%',
        position: 'relative',
      }}
    >
      <Stack
        direction='column'
        alignItems='center'
        justifyContent='center'
        gap={theme.spacing(1)}
        sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}
      >
        <Typography color={theme.palette.primary.main} variant='displayBold' align='center'>
          {firstName || 'User'},
        </Typography>
        <Typography variant='titleMedium' color='textSecondary' align='center'>
          {greeting}
        </Typography>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          '& > div': {
            position: 'relative !important' as 'relative',
            bottom: 'auto !important' as 'auto',
            left: 'auto !important' as 'auto',
            right: 'auto !important' as 'auto',
            width: '100%',
          },
        }}
      >
        <ChatControls
          handleSubmit={handleSubmit}
          input={input}
          isLoading={isLoading}
          isStreaming={isStreaming}
          isReady={isReady}
          setInput={setInput}
        />
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          gap={theme.spacing(0.5)}
          sx={{ paddingTop: theme.spacing(2) }}
        >
          <Typography variant='paragraphMedium' textAlign='center'>
            Try these ✨
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.spacing(0.5),
              justifyContent: 'center',
              padding: theme.spacing(1),
            }}
          >
            {samplePrompts.map((prompt, index) => (
              <Chip
                key={`${prompt.displayPrompt}-${index}`}
                label={prompt.displayPrompt}
                onClick={() => handlePromptClick(prompt)}
                clickable
                variant='outlined'
                size='small'
                disabled={isLoading || isStreaming || !isReady}
                sx={{
                  height: '28px',
                  '& .MuiChip-label': {
                    ...theme.typography.captionRegular,
                  },
                  backgroundColor: theme.palette.background.paper,
                  borderColor: theme.palette.border.light,
                  cursor: isLoading || isStreaming || !isReady ? 'not-allowed' : 'pointer',
                }}
              />
            ))}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default ChatDefaultState;
